import { CircularProgress } from '@mui/material';
import classNames from 'classnames';
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  TouchEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { removeError } from 'src/features/globalSlice';
import { accessTicketPIN } from 'src/store/actions/ticketActions';
import { Modal } from 'src/store/models/Modal';
import {
  useErrorSelector,
  useLoadingSelector,
} from 'src/utils/useLoadingSelector';

import Button from '../Button/Button';
import classes from './PinModal.module.scss';

export interface PinModalProps {
  modal?: Modal;
  transitionDuration: number;
}

const PinModal: FC<PinModalProps> = ({ modal, transitionDuration }) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement[]>([]);
  const [values, setValues] = useState<string[]>(['', '', '', '']);

  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const loading = useLoadingSelector({
    actionType: accessTicketPIN.toString(),
  });
  const error = useErrorSelector({ actionType: accessTicketPIN.toString() });

  const list = [...Array(4)];

  const handler = (e: ChangeEvent<HTMLInputElement>, i: number) => {
    const next = inputRef.current[i + 1];
    setValues((prev) =>
      prev.map((v, index) => (index === i ? e.target?.value.slice(0, 1) : v))
    );
    dispatch(removeError({ actionType: accessTicketPIN.toString() }));

    if (e.target?.value) {
      if (next) {
        next.focus();
      } else {
        const pin = list
          .map((_, i) => inputRef.current?.[i]?.value)
          ?.join('')
          ?.slice(0, 4);
        dispatch(
          accessTicketPIN({ unitId: ticket?.unitId, ticketId: ticket?.id, pin })
        );
      }
    }
  };

  const focusFirstEmpty = (
    e?: MouseEvent<HTMLInputElement> | TouchEvent<HTMLInputElement>
  ) => {
    e?.preventDefault();
    if (inputRef.current && Array.isArray(inputRef.current)) {
      for (const input of inputRef.current) {
        if (!input?.value) {
          input?.focus();
          break;
        }
      }
      const length = inputRef?.current?.length;
      if (inputRef.current?.[length - 1]?.value) {
        inputRef.current?.[length - 1]?.focus();
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      inputRef?.current?.[0]?.focus();
    }, transitionDuration);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    focusFirstEmpty();
  }, [loading]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e?.key === 'Backspace' && i !== 0) {
      dispatch(removeError({ actionType: accessTicketPIN.toString() }));
      e?.preventDefault();
      if (!e?.currentTarget?.value) {
        const prev = inputRef.current[i - 1];
        setValues((p) => p.map((v, index) => (index === i - 1 ? '' : v)));
        prev?.focus();
      } else {
        setValues((p) => p.map((v, index) => (index === i ? '' : v)));
      }
    }
  };

  return (
    <div className={classes.Container}>
      <h4>{modal?.title}</h4>
      <p>{modal?.body}</p>
      <section>
        <div className={classNames({ [classes.Error]: error })}>
          {!loading ? (
            list.map((_, i) => (
              <input
                key={i}
                ref={(el) => (inputRef.current[i] = el)}
                value={values[i]}
                onChange={(e) => handler(e, i)}
                type="number"
                min={0}
                max={9}
                onMouseDown={focusFirstEmpty}
                onTouchStart={focusFirstEmpty}
                onKeyDown={(e) => handleKeyDown(e, i)}
              />
            ))
          ) : (
            <>
              <CircularProgress color="primary" />
            </>
          )}
        </div>
        {error ? <p>{error?.errorMessage}</p> : null}
      </section>
      <Button onClick={modal?.secondaryAction} variant="outlined">
        {modal?.secondaryButtonText}
      </Button>
    </div>
  );
};

export default PinModal;
